package com.zi.support.web

import com.zi.support.shared.DataUsageDay
import com.zi.support.shared.DeviceStatus
import com.zi.support.shared.DriverLogEntry
import com.zi.support.shared.DriverLogs
import kotlinx.browser.document
import org.w3c.dom.HTMLElement

/** More > Driver app logs: every Driver Hub Complete attempt, by company, then Production or Test. */
internal object DriverLogsScreen {
    private const val PAGE_SIZE = DriverLogs.PAGE_SIZE
    private const val USAGE_EXPORT = "<button type=\"button\" id=\"usage-export\" class=\"btn btn-primary\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">picture_as_pdf</span>Export PDF</button>"

    /**
     * The Data usage popup's ("usage") and Insights' ("ins") filters as labelled rows: Period (with its date, or
     * From / To and Clear), then for the popup Show (scope, driver, phone), then Group by.
     */
    private fun usageFilters(id: String, withDriver: Boolean): String {
        fun row(label: String, body: String) = "<div class=\"du-row\"><span class=\"du-label\">$label</span><div class=\"du-controls\">$body</div></div>"
        return "<div class=\"dl-usage-filters\" id=\"$id-filters\">" +
            row("Period", "<div class=\"segmented\" role=\"group\" aria-label=\"Period\">" +
                DriverLogs.usagePeriods.joinToString("") { "<button type=\"button\" data-uperiod=\"$it\">$it</button>" } + "</div>" +
                "<label class=\"u-day\"><span class=\"visually-hidden\">Date</span><input id=\"$id-day\" type=\"date\" class=\"input\"></label>" +
                "<label class=\"u-range\" hidden><span>From</span><input id=\"$id-from\" type=\"date\" class=\"input\"></label>" +
                "<label class=\"u-range\" hidden><span>To</span><input id=\"$id-to\" type=\"date\" class=\"input\"></label>" +
                "<button type=\"button\" id=\"$id-clear\" class=\"btn u-range\" hidden>Clear</button>") +
            (if (withDriver) row("Show", "<div class=\"segmented\" role=\"group\" aria-label=\"Scope\">" +
                DriverLogs.usageScopes.joinToString("") { "<button type=\"button\" data-uscope=\"$it\">$it</button>" } + "</div>" +
                "<label><span class=\"visually-hidden\">Driver</span><select id=\"$id-driver\" class=\"input\"></select></label>" +
                "<label><span class=\"visually-hidden\">Phone</span><select id=\"$id-device\" class=\"input\"></select></label>") else "") +
            row("Group by", "<div class=\"segmented\" role=\"group\" aria-label=\"Group by\">" +
                (if (withDriver) DriverLogs.usageReportViews else DriverLogs.usageViews).joinToString("") { "<button type=\"button\" data-uview=\"$it\">${DriverLogs.usageViewLabel(it)}</button>" } + "</div>") +
            "</div>"
    }

    fun render() {
        fun tabs(id: String, options: List<String>, label: String) =
            "<div id=\"$id\" class=\"segmented apps-tabs\" role=\"tablist\" aria-label=\"$label\">" +
                options.joinToString("") { "<button role=\"tab\" data-tab=\"${escape(it)}\">${escape(it)}</button>" } + "</div>"
        element("content")?.innerHTML = """
          $MORE_BACK
          <div class="page-intro"><div><h2>Driver app logs</h2>
            <p>Every time Driver Hub completes a booking or loads the bookings list, whether it went through or failed.</p></div></div>
          ${tabs("logs-kind", DriverLogs.tabs, "Log type")}
          <div class="dl-filters">${tabs("logs-company", DriverLogs.companies, "Company")}${tabs("logs-env", DriverLogs.environments, "Environment")}</div>
          <section id="logs-insights" class="dl-insights" hidden>
            ${usageFilters("ins", withDriver = false)}
            <h3 id="ins-span" class="dl-usage-span"></h3>
            <div id="ins-tiles" class="dl-ins-tiles"></div>
            <div class="dl-ins-card"><header><h4 id="ins-title"></h4><p id="ins-sub"></p></header>
              <ul id="ins-legend" class="dl-ins-legend" aria-label="Legend"></ul><div id="ins-chart" class="dl-ins-chart"></div></div>
            <details class="dl-ins-table"><summary>Show as a table</summary><div id="ins-table"></div></details>
          </section>
          <div id="logs-section">
          <div class="search-shell dl-search"><span class="material-symbols-rounded" aria-hidden="true">search</span><label class="visually-hidden" for="logs-search">Search logs</label><input id="logs-search" class="input search" placeholder="Search driver, booking, vehicle or error"></div>
          <div class="dl-filter-row">
            <label class="dl-driver"><span class="material-symbols-rounded" aria-hidden="true">person</span><span class="visually-hidden">Driver</span><select id="logs-driver" class="input"><option value="">All drivers</option></select></label>
            <div id="logs-range" class="segmented dl-range" role="group" aria-label="Date">${DriverLogs.dateRanges.joinToString("") { "<button type=\"button\" data-range=\"${escape(it)}\">${escape(it)}</button>" }}</div>
            <div id="logs-custom" class="dl-custom" hidden><label>From <input id="logs-from" type="date" class="input"></label><label>To <input id="logs-to" type="date" class="input"></label><button type="button" id="logs-clear" class="btn">Clear</button></div>
          </div>
          <section class="dl-stats"><div id="logs-counts" class="dl-counts"></div><p id="logs-summary">Loading…</p></section>
          <button type="button" id="logs-usage-open" class="btn dl-usage-open"><span class="material-symbols-rounded" aria-hidden="true">data_usage</span>Data usage</button>
          <div id="logs-list" class="dl-list"></div>
          </div>
        """.trimIndent()
        bindMoreBack()
        var kind = DriverLogs.COMPLETE
        var company = DriverLogs.companies.first()
        var environment = DriverLogs.PRODUCTION
        var servers = emptyList<String>()
        /** Server id → the access code's company name, which decides its tabs. */
        var names = emptyMap<String, String>()
        var shown = emptyList<String>()
        var logs = emptyList<DriverLogEntry>()
        var page = 0
        /** One search per company + environment tab, kept when switching between them. */
        val searches = mutableMapOf<String, String>()
        /** Driver filter per tab (each tab has its own drivers); the date filter applies to every tab. */
        val driverPicks = mutableMapOf<String, String>()
        var range = DriverLogs.ANY_TIME
        var fromDay = ""
        var toDay = ""
        var driverOptions = emptyList<String>()
        /** The Data usage popup's own filters: Day, Week or Month around [usageDay], and a driver (blank = everyone). */
        var usagePeriod = DriverLogs.USAGE_DAY
        var usageDay = DriverLogs.today(kotlin.js.Date.now().toLong())
        var usageDriver = ""
        var usageView = DriverLogs.BY_DRIVER
        /** The popup's phone filter (installationId, blank = every phone) and This company / Whole app. */
        var usageDevice = ""
        var usageScope = DriverLogs.USAGE_THIS_COMPANY
        /** Custom range ends ("" = the picked day). */
        var usageFrom = ""
        var usageTo = ""
        fun span() = DriverLogs.usageSpan(usagePeriod, usageDay, usageFrom, usageTo)
        val usageStops = mutableListOf<dynamic>()
        var usageVersion = 0
        fun tabKey() = "$kind|$company|$environment"
        /** Server → its logs by id: what this browser has cached, plus anything newer, plus older ones read for a search. */
        val byServer = mutableMapOf<String, MutableMap<String, DriverLogEntry>>()
        /** Server → its usage records (one per phone per day) by id. */
        val usageByServer = mutableMapOf<String, Map<String, DataUsageDay>>()
        /** The tab's first search reads older logs once (searchedTab); searchingOlder is true while that read runs. */
        var searchedTab = ""
        var searchingOlder = false
        var searchOlderRef: () -> Unit = {}
        var listenUsageRef: () -> Unit = {}
        val stops = mutableListOf<dynamic>()
        var stopServers: dynamic = null

        /** Marks the picked view and period in a filter row and shows the day or From/To fields. */
        fun syncUsageFilters(id: String) {
            val box = element("$id-filters") ?: return
            box.querySelectorAll("[data-uview], [data-uperiod], [data-uscope]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).let {
                val view = if (id == "ins" && usageView !in DriverLogs.usageViews) DriverLogs.BY_DRIVER else usageView
                val on = it.getAttribute("data-uview") == view || it.getAttribute("data-uperiod") == usagePeriod
                val lit = on || it.getAttribute("data-uscope") == usageScope
                it.classList.toggle("active", lit); it.setAttribute("aria-pressed", lit.toString())
            } }
            val custom = usagePeriod == DriverLogs.USAGE_CUSTOM
            box.querySelectorAll(".u-day").let { for (i in 0 until it.length) (it.item(i) as HTMLElement).hidden = custom }
            box.querySelectorAll(".u-range").let { for (i in 0 until it.length) (it.item(i) as HTMLElement).hidden = !custom }
            val (from, to) = span()
            fun set(field: String, value: String) { (element("$id-$field") as? org.w3c.dom.HTMLInputElement)?.let { if (it.value != value) it.value = value } }
            set("day", usageDay); set("from", from); set("to", to)
        }

        /** The usage records read, limited to the open tab's servers unless [scope] is Whole app. */
        fun scopedUsage(scope: String) = usageByServer.values.flatMap { it.values }.filter { scope == DriverLogs.USAGE_WHOLE_APP || it.server in shown }

                /** Fills the Data usage popup when it's open: its filters, then one card per driver for the period. */
        fun renderUsage() {
            val list = element("logs-usage") ?: return
            val (from, to) = span()
            val all = scopedUsage(usageScope)
            syncUsageFilters("usage")
            element("usage-span")?.textContent = DriverLogs.usageSpanLabel(usagePeriod, usageDay, kotlin.js.Date.now().toLong(), usageFrom, usageTo)
            element("usage-total")?.textContent = DriverLogs.usageTotal(all, usageDriver, from, to, usageDevice)
            element("usage-counts")?.textContent = DriverLogs.usageCountsLabel(all, usageDriver, from, to, usageDevice)
            (element("usage-driver") as? org.w3c.dom.HTMLSelectElement)?.let { select ->
                val drivers = (DriverLogs.usageDrivers(all, from, to) + listOf(usageDriver).filter { it.isNotBlank() }).distinct().sortedBy { it.lowercase() }
                select.innerHTML = "<option value=\"\">All drivers</option>" + drivers.joinToString("") { "<option value=\"${escape(it)}\">${escape(it)}</option>" }
                select.value = usageDriver
            }
            (element("usage-device") as? org.w3c.dom.HTMLSelectElement)?.let { select ->
                val devices = DriverLogs.usageDevices(all, from, to).let { list -> if (usageDevice.isBlank() || list.any { it.first == usageDevice }) list else list + (usageDevice to DriverLogs.usageDeviceLabel(all, usageDevice)) }
                select.innerHTML = "<option value=\"\">All phones</option>" + devices.joinToString("") { "<option value=\"${escape(it.first)}\">${escape(it.second)}</option>" }
                select.value = usageDevice
            }
            val usage = DriverLogs.usageRows(all, usageDriver, from, to, usageView, usageDevice)
            list.innerHTML = if (usage.isEmpty()) "<p class=\"dl-empty\">No data usage recorded for this ${usagePeriod.lowercase()}${if (usageDriver.isBlank()) "" else " for ${escape(usageDriver)}"}.</p>"
                else usage.joinToString("") { row ->
                    "<article class=\"dl-usage-card\"><header><div><strong>${escape(row.name)}</strong><small>${escape(row.detail)}</small></div>" +
                        "<b>${escape(row.total)}</b></header>" +
                        "<div class=\"dl-usage-bar\" aria-hidden=\"true\">" + row.parts.withIndex().joinToString("") { (i, part) ->
                            "<span class=\"part-$i\" style=\"width:${(part.share * 100).coerceIn(0.0, 100.0)}%\"></span>" } + "</div>" +
                        "<ul>" + row.parts.withIndex().joinToString("") { (i, part) ->
                            "<li title=\"${escape(part.label)}\"><i class=\"part-$i\"></i><b>${escape(part.size)}</b><small>${escape(part.label)} · ${escape(part.count)}</small></li>" } + "</ul>" +
                        (if (row.androidTotal.isBlank()) "" else "<p class=\"dl-usage-android\">Android counted <b>${escape(row.androidTotal)}</b> for the whole app</p>") +
                        "</article>"
                }
        }

        /** Driver app logs > Insights: totals, then one stacked bar per driver (by phone) or per phone (by driver). */
        fun renderInsights() {
            if (kind != DriverLogs.INSIGHTS) return
            val chart = element("ins-chart") ?: return
            val (from, to) = span()
            syncUsageFilters("ins")
            element("ins-span")?.textContent = DriverLogs.usageSpanLabel(usagePeriod, usageDay, kotlin.js.Date.now().toLong(), usageFrom, usageTo)
            val data = DriverLogs.insights(scopedUsage(DriverLogs.USAGE_THIS_COMPANY), from, to, if (usageView in DriverLogs.usageViews) usageView else DriverLogs.BY_DRIVER)
            val byDevice = usageView == DriverLogs.BY_DEVICE
            element("ins-tiles")?.innerHTML = listOf("Data used" to data.total, "Drivers" to "${data.drivers}", "Phones" to "${data.devices}")
                .joinToString("") { (label, value) -> "<div class=\"dl-ins-tile\"><span>$label</span><strong>${escape(value)}</strong></div>" }
            element("ins-title")?.textContent = if (byDevice) "Data used per phone" else "Data used per driver"
            element("ins-sub")?.textContent = if (byDevice) "Each bar is one phone, split by the drivers who used it." else "Each bar is one driver, split by the phones they used."
            element("ins-legend")?.innerHTML = data.legend.joinToString("") { "<li><i class=\"s-${it.slot}\" aria-hidden=\"true\"></i>${escape(it.label)}</li>" }
            chart.innerHTML = if (data.bars.isEmpty()) "<p class=\"dl-empty\">No data usage recorded for this period in $company ${environment.lowercase()}. Usage is recorded by the latest Driver Hub.</p>"
            else data.bars.joinToString("") { bar ->
                val parts = bar.segments.joinToString(", ") { "${it.label} ${it.size}" }
                "<div class=\"ins-row\"><div class=\"ins-label\"><strong>${escape(bar.label)}</strong><small>${escape(bar.detail)}</small></div>" +
                    "<div class=\"ins-track\"><div class=\"ins-bar\" role=\"img\" aria-label=\"${escape("${bar.label}: ${bar.total} ($parts)")}\" style=\"width:${(bar.width * 100).coerceIn(1.0, 100.0)}%\">" +
                    bar.segments.joinToString("") { seg ->
                        "<span class=\"s-${seg.slot}\" style=\"flex-grow:${seg.share}\" title=\"${escape("${seg.label} · ${seg.size} (${(seg.share * 100).toInt()}%)")}\"></span>"
                    } + "</div><b>${escape(bar.total)}</b></div></div>"
            }
            element("ins-table")?.innerHTML = if (data.bars.isEmpty()) "" else "<table><thead><tr><th>${if (byDevice) "Phone" else "Driver"}</th><th>${if (byDevice) "Driver" else "Phone"}</th><th>Data</th></tr></thead><tbody>" +
                data.bars.joinToString("") { bar -> bar.segments.withIndex().joinToString("") { (i, seg) ->
                    "<tr><td>${if (i == 0) escape(bar.label) else ""}</td><td>${escape(seg.label)}</td><td>${escape(seg.size)}</td></tr>" } } + "</tbody></table>"
        }

        fun markTabs(id: String, selected: String) {
            val buttons = element(id)?.querySelectorAll("button") ?: return
            for (i in 0 until buttons.length) (buttons.item(i) as HTMLElement).let {
                val on = it.getAttribute("data-tab") == selected
                it.classList.toggle("active", on); it.setAttribute("aria-selected", on.toString())
            }
        }

        fun render() {
            // Leaving the screen replaces the page: once the list is gone, stop listening.
            val list = element("logs-list") ?: run { stops.forEach { it() }; if (stopServers != null) stopServers(); return }
            markTabs("logs-kind", kind); markTabs("logs-company", company); markTabs("logs-env", environment)
            val insights = kind == DriverLogs.INSIGHTS
            (element("logs-insights") as? HTMLElement)?.hidden = !insights
            (element("logs-section") as? HTMLElement)?.hidden = insights
            if (insights) { renderInsights(); return }
            val all = byServer.values.flatMap { it.values }
            val needle = searches[tabKey()].orEmpty().trim()
            val driver = driverPicks[tabKey()].orEmpty()
            val filtering = DriverLogs.isFiltering(needle, driver, range)
            if (filtering) searchOlderRef()
            // Only the newest 200 without filters; a search, driver or date covers older logs too.
            logs = DriverLogs.filtered(all, needle, driver, range, kotlin.js.Date.now().toLong(), fromDay, toDay)
            val drivers = (DriverLogs.drivers(all) + listOf(driver).filter { it.isNotBlank() }).distinct().sortedBy { it.lowercase() }
            (element("logs-driver") as? org.w3c.dom.HTMLSelectElement)?.let { select ->
                if (drivers != driverOptions) {
                    driverOptions = drivers
                    select.innerHTML = "<option value=\"\">All drivers</option>" + drivers.joinToString("") { "<option value=\"${escape(it)}\">${escape(it)}</option>" }
                }
                select.value = driver
                select.parentElement?.classList?.toggle("on", driver.isNotBlank())
            }
            document.querySelectorAll("[data-range]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).classList.toggle("active", nodes.item(i).asDynamic().getAttribute("data-range") == range) }
            (element("logs-custom") as? HTMLElement)?.hidden = range != DriverLogs.CUSTOM
            renderUsage()
            val failed = logs.count { it.outcome == "failed" }
            element("logs-counts")?.innerHTML = (listOf("Total" to logs.size, DriverLogs.successLabel(kind) to logs.count { it.outcome == "success" }, "Failed" to failed) +
                listOfNotNull((DriverLogs.SAVED_OFFLINE to DriverLogs.savedOfflineCount(logs)).takeIf { kind == DriverLogs.COMPLETE }))
                .joinToString("") { (label, n) -> "<div class=\"dl-count dl-${when (label) { "Failed", "Total" -> label.lowercase(); DriverLogs.SAVED_OFFLINE -> "offline"; else -> "completed" }}\"><strong>$n</strong><span>$label</span></div>" }
            element("logs-summary")?.textContent = if (shown.isEmpty()) "No logs from $company ${environment.lowercase()} yet."
                else "From ${shown.joinToString(", ") { DeviceStatus.serverLabel(it) }}" + when {
                    searchingOlder -> " · searching older logs…"
                    !filtering -> " · newest ${minOf(all.size, DriverLogs.LATEST_SHOWN)} (search or filter to find older ones)"
                    else -> " · ${logs.size} match"
                }
            val pages = maxOf(1, (logs.size + PAGE_SIZE - 1) / PAGE_SIZE)
            page = page.coerceIn(0, pages - 1)
            val first = page * PAGE_SIZE
            val icons = mapOf("success" to "check", "offline" to "cloud_done", "failed" to "close", "pending" to "schedule")
            list.innerHTML = if (logs.isEmpty()) "<p class=\"dl-empty\">${when { shown.isEmpty() -> "Nothing to show here yet."; needle.isNotBlank() -> "No logs match “${escape(needle)}”."; filtering -> "No logs match these filters."; else -> DriverLogs.emptyText(kind) }}</p>"
            else logs.withIndex().drop(first).take(PAGE_SIZE).joinToString("") { (index, log) ->
                val tone = DriverLogs.tone(log)
                "<button class=\"dl-row tone-$tone\" data-log=\"$index\"><span class=\"dl-head\">" +
                    "<span class=\"dl-icon material-symbols-rounded\" aria-hidden=\"true\">${icons[tone]}</span>" +
                    "<span class=\"dl-main\"><strong>${escape(DriverLogs.title(log))}</strong><small>${escape(DriverLogs.subtitle(log))}</small></span>" +
                    "<span class=\"dl-pill\">${escape(DriverLogs.outcomeLabel(log))}</span></span>" +
                    DriverLogs.offlineNote(log).let { if (it.isBlank()) "" else "<span class=\"dl-note dl-note-offline\">${escape(it)}</span>" } +
                    (if (log.error.isBlank()) "" else "<span class=\"dl-note dl-note-error\">${escape(log.error.take(200))}</span>") +
                    "</button>"
            } + (if (pages > 1) "<div class=\"calls-pager\"><button type=\"button\" class=\"btn\" data-log-page=\"${page - 1}\"${if (page == 0) " disabled" else ""}>‹ Previous</button>" +
                "<span>${first + 1}–${minOf(first + PAGE_SIZE, logs.size)} of ${logs.size} · page ${page + 1} of $pages</span>" +
                "<button type=\"button\" class=\"btn\" data-log-page=\"${page + 1}\"${if (page >= pages - 1) " disabled" else ""}>Next ›</button></div>" else "")

            val pagers = list.querySelectorAll("[data-log-page]")
            for (i in 0 until pagers.length) (pagers.item(i) as HTMLElement).addEventListener("click", { e ->
                page = (e.currentTarget as HTMLElement).getAttribute("data-log-page")!!.toInt(); render()
                element("logs-search")?.asDynamic()?.scrollIntoView(js("({block:'start'})"))
            })
            val rows = list.querySelectorAll("[data-log]")
            for (i in 0 until rows.length) (rows.item(i) as HTMLElement).let { row ->
                row.addEventListener("click", {
                    val log = logs[row.getAttribute("data-log")!!.toInt()]
                    modal(DriverLogs.title(log), "<pre class=\"log-details\">${escape(DriverLogs.details(log))}</pre>", wide = true, layered = true)
                })
            }
        }

        fun entry(id: String, data: dynamic) = DriverLogs.entry(id, { field(data, it) }, { (data[it] as? Number)?.toLong() ?: 0L },
            { name -> (data[name] as? Array<*>)?.mapNotNull { (it as? Number)?.toLong() }.orEmpty() },
            { name -> (data[name] as? Array<*>)?.mapNotNull { it as? String }.orEmpty() },
            { name -> data[name] == true },
            { timestampMillis(data[it]).toLong() }, kind)

        fun add(server: String, snapshot: dynamic) {
            val docs: dynamic = snapshot.docs
            val logs = byServer.getOrPut(server) { mutableMapOf() }
            for (i in 0 until (docs.length as Int)) logs[docs[i].id as String] = entry(docs[i].id as String, docs[i].data())
        }

        fun logsOf(server: String) = FirebaseGateway.collection("driver_logs/$server/${DriverLogs.collection(kind)}")

        /** Day / Week / Month / Custom and By driver / By device, shared by the Data usage popup and Insights. */
        fun bindUsageFilters(id: String) {
            val box = element("$id-filters") ?: return
            box.querySelectorAll("[data-uview]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).addEventListener("click", { e ->
                usageView = (e.currentTarget as HTMLElement).getAttribute("data-uview") ?: DriverLogs.BY_DRIVER; renderUsage(); renderInsights()
            }) }
            box.querySelectorAll("[data-uperiod]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).addEventListener("click", { e ->
                usagePeriod = (e.currentTarget as HTMLElement).getAttribute("data-uperiod") ?: DriverLogs.USAGE_DAY; listenUsageRef()
            }) }
            change("$id-day") { input("$id-day").takeIf { it.isNotBlank() }?.let { usageDay = it; listenUsageRef() } }
            change("$id-from") { usageFrom = input("$id-from"); listenUsageRef() }
            change("$id-to") { usageTo = input("$id-to"); listenUsageRef() }
            // Clear: back to today, with no custom range.
            click("$id-clear") { usagePeriod = DriverLogs.USAGE_DAY; usageDay = DriverLogs.today(kotlin.js.Date.now().toLong()); usageFrom = ""; usageTo = ""; listenUsageRef() }
            element("$id-driver")?.addEventListener("change", { usageDriver = (element("$id-driver") as org.w3c.dom.HTMLSelectElement).value; renderUsage() })
            element("$id-device")?.addEventListener("change", { usageDevice = (element("$id-device") as org.w3c.dom.HTMLSelectElement).value; renderUsage() })
            box.querySelectorAll("[data-uscope]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).addEventListener("click", { e ->
                usageScope = (e.currentTarget as HTMLElement).getAttribute("data-uscope") ?: DriverLogs.USAGE_THIS_COMPANY; listenUsageRef()
            }) }
        }

        fun usageEntry(id: String, data: dynamic): DataUsageDay {
            val endpoints: dynamic = data.endpoints ?: js("({})")
            val keys = js("Object.keys")(endpoints) as Array<String>
            return DriverLogs.usageEntry(id, { field(data, it) }, { (data[it] as? Number)?.toLong() ?: 0L }, keys.associateWith { key ->
                val counts: dynamic = endpoints[key]
                listOf("calls", "sentBytes", "receivedBytes").associateWith { ((if (counts == null) null else counts[it]) as? Number)?.toLong() ?: 0L }
            })
        }

        /** Usage records are counters that change all day, so the popup's and Insights' period is listened to in full. */
        fun listenUsage() {
            usageStops.forEach { it() }; usageStops.clear(); usageByServer.clear()
            val (from, to) = span()
            // A newer listen (another tab or period) makes this one's late answers stale.
            val version = ++usageVersion
            fun current() = version == usageVersion
            val fs = FirebaseGateway.firestore
            // Whole app reads every server's usage; This company (and Insights) only the open tab's.
            (if (usageScope == DriverLogs.USAGE_WHOLE_APP) servers else shown).forEach { server ->
                usageStops.add(fs.onSnapshot(fs.query(FirebaseGateway.collection("driver_logs/$server/usage"), fs.where("date", ">=", from), fs.where("date", "<=", to)),
                    { snapshot: dynamic ->
                        if (current()) {
                            val docs: dynamic = snapshot.docs
                            usageByServer[server] = DriverLogs.withServer((0 until (docs.length as Int)).map { usageEntry(docs[it].id as String, docs[it].data()) }, server, names[server].orEmpty()).associateBy { it.id }
                            renderUsage(); renderInsights()
                        }
                    }, { error: dynamic -> (element("logs-usage") ?: element("ins-chart"))?.textContent = FirebaseGateway.errorMessage(error) }))
            }
            renderUsage(); renderInsights()
        }
        listenUsageRef = ::listenUsage

        /** Data usage > Export PDF: the popup's rows for its filters, rendered on the server (exportUsagePdf). */
        fun exportUsage() {
            val button = element("usage-export") ?: return
            val (from, to) = span()
            val all = scopedUsage(usageScope)
            val rows = DriverLogs.usageRows(all, usageDriver, from, to, usageView, usageDevice).map { row ->
                json("name" to row.name, "detail" to row.detail, "total" to row.total, "android" to row.androidTotal,
                    "parts" to row.parts.map { json("label" to it.label, "count" to it.count, "size" to it.size) }.toTypedArray())
            }
            val request = json("view" to usageView, "period" to DriverLogs.usageSpanLabel(usagePeriod, usageDay, kotlin.js.Date.now().toLong(), usageFrom, usageTo),
                "scope" to DriverLogs.usageScopeLabel(usageScope, company, environment, usageDriver, DriverLogs.usageDeviceLabel(all, usageDevice)),
                "total" to DriverLogs.usageTotal(all, usageDriver, from, to, usageDevice), "rows" to rows.toTypedArray(),
                "counts" to DriverLogs.usageCounts(all, usageDriver, from, to, usageDevice).let { (d, p, c) -> json("drivers" to d, "phones" to p, "companies" to c) })
            button.setAttribute("disabled", ""); button.innerHTML = "<span class=\"spinner-mini\"></span> Building PDF…"
            fun reset() { button.removeAttribute("disabled"); button.innerHTML = USAGE_EXPORT.substringAfter(">").substringBeforeLast("<") }
            promise(FirebaseGateway.callable("exportUsagePdf", request), { result ->
                Infographic.downloadPdf(result.data.pdfBase64 as String, result.data.fileName as String); toast("PDF downloaded."); reset()
            }, { message -> toast(message, true); reset() })
        }

        /**
         * Per server: show what this browser already has (no reads), then listen only for logs newer than the
         * newest one seen, so reopening a tab costs one read per new log. Logs are only ever added (Driver Hub
         * writes each attempt once, with a server createdAt), so nothing older can change. First visit: newest page.
         * Insights reads only the usage records.
         */
        fun listenToLogs() {
            stops.forEach { it() }; stops.clear(); byServer.clear(); searchedTab = ""; searchingOlder = false
            val (wantCompany, wantEnvironment) = company to environment
            val wantKind = kind
            fun current() = kind == wantKind && company == wantCompany && environment == wantEnvironment
            shown = servers.filter { DriverLogs.classify(it, names[it].orEmpty()) == wantCompany to wantEnvironment }
            val fs = FirebaseGateway.firestore
            if (kind != DriverLogs.INSIGHTS) shown.forEach { server ->
                fun listenNewer() {
                    if (!current()) return
                    val newest = byServer[server]?.values?.maxOfOrNull { it.createdMillis } ?: 0L
                    val query = if (newest > 0) fs.query(logsOf(server), fs.where("createdAt", ">", FirebaseGateway.timestampFromMillis(newest.toDouble())), fs.orderBy("createdAt", "desc"))
                        else fs.query(logsOf(server), fs.orderBy("createdAt", "desc"), fs.limit(DriverLogs.LATEST_SHOWN))
                    stops.add(fs.onSnapshot(query, { snapshot: dynamic -> if (current()) { add(server, snapshot); render() } },
                        { error: dynamic -> element("logs-summary")?.textContent = FirebaseGateway.errorMessage(error) }))
                }
                promise(fs.getDocsFromCache(fs.query(logsOf(server), fs.orderBy("createdAt", "desc"))), { snapshot ->
                    if (current()) { add(server, snapshot); render(); listenNewer() }
                }, { listenNewer() })
            }
            listenUsage()
            render()
        }

        /**
         * Older logs, read once per tab and filter: a date filter reads every log in its range ([DriverLogs.RANGE_READ]
         * at most); a search or driver filter without one reads the newest [DriverLogs.SEARCH_SCAN].
         */
        fun searchOlder() {
            val key = DriverLogs.readKey(tabKey(), range, fromDay, toDay)
            if (searchedTab == key || shown.isEmpty()) return
            searchedTab = key; searchingOlder = true
            val wantTab = tabKey()
            val fs = FirebaseGateway.firestore
            val dates = DriverLogs.readRange(range, kotlin.js.Date.now().toLong(), fromDay, toDay)
            var left = shown.size
            shown.forEach { server ->
                val query = if (dates == null) fs.query(logsOf(server), fs.orderBy("createdAt", "desc"), fs.limit(DriverLogs.SEARCH_SCAN))
                    else if (dates.untilMillis == null) fs.query(logsOf(server), fs.where("createdAt", ">=", FirebaseGateway.timestampFromMillis(dates.fromMillis.toDouble())),
                        fs.orderBy("createdAt", "desc"), fs.limit(DriverLogs.RANGE_READ))
                    else fs.query(logsOf(server), fs.where("createdAt", ">=", FirebaseGateway.timestampFromMillis(dates.fromMillis.toDouble())),
                        fs.where("createdAt", "<", FirebaseGateway.timestampFromMillis(dates.untilMillis!!.toDouble())), fs.orderBy("createdAt", "desc"), fs.limit(DriverLogs.RANGE_READ))
                promise(fs.getDocs(query), { snapshot ->
                    if (tabKey() == wantTab) add(server, snapshot)
                    if (--left == 0 && tabKey() == wantTab) { searchingOlder = false; render() }
                }, { message -> toast(message, true); if (--left == 0 && tabKey() == wantTab) { searchingOlder = false; render() } })
            }
        }

        searchOlderRef = ::searchOlder

        fun onTab(id: String, pick: (String) -> Unit) {
            val buttons = element(id)?.querySelectorAll("button") ?: return
            for (i in 0 until buttons.length) (buttons.item(i) as HTMLElement).let { button ->
                button.addEventListener("click", {
                    pick(button.getAttribute("data-tab").orEmpty()); page = 0
                    (element("logs-search") as? org.w3c.dom.HTMLInputElement)?.value = searches[tabKey()].orEmpty()
                    listenToLogs()
                })
            }
        }
        element("logs-usage-open")?.addEventListener("click", {
            modal("Data usage", usageFilters("usage", withDriver = true) +
                "<div class=\"du-summary\"><div><span id=\"usage-span\"></span><strong id=\"usage-total\"></strong><small id=\"usage-counts\"></small></div>" +
                "<ul class=\"du-legend\" aria-label=\"Colour key\"><li><i class=\"part-0\"></i>Loading bookings</li><li><i class=\"part-1\"></i>Completing bookings (with photos)</li><li><i class=\"part-2\"></i>Everything else</li></ul></div>" +
                "<div id=\"logs-usage\"></div>", USAGE_EXPORT, wide = true)
            bindUsageFilters("usage")
            click("usage-export") { exportUsage() }
            renderUsage()
        })
        element("logs-driver")?.addEventListener("change", {
            val pick = (element("logs-driver") as org.w3c.dom.HTMLSelectElement).value
            if (pick.isBlank()) driverPicks.remove(tabKey()) else driverPicks[tabKey()] = pick
            page = 0; render()
        })
        document.querySelectorAll("[data-range]").let { nodes -> for (i in 0 until nodes.length) (nodes.item(i) as HTMLElement).addEventListener("click", { e ->
            range = (e.currentTarget as HTMLElement).getAttribute("data-range") ?: DriverLogs.ANY_TIME; page = 0; render()
        }) }
        change("logs-from") { fromDay = input("logs-from"); page = 0; render() }
        change("logs-to") { toDay = input("logs-to"); page = 0; render() }
        // Clear: drop the custom range and go back to Any time.
        click("logs-clear") {
            range = DriverLogs.ANY_TIME; fromDay = ""; toDay = ""; page = 0
            (element("logs-from") as? org.w3c.dom.HTMLInputElement)?.value = ""; (element("logs-to") as? org.w3c.dom.HTMLInputElement)?.value = ""
            render()
        }
        type("logs-search") { searches[tabKey()] = input("logs-search"); page = 0; render() }
        bindSearchClear("logs-search") { searches.remove(tabKey()); page = 0; render() }
        onTab("logs-kind") { kind = it }
        bindUsageFilters("ins")
        onTab("logs-company") { company = it }
        onTab("logs-env") { environment = it }
        stopServers = FirebaseGateway.listenQuery(FirebaseGateway.firestore.query(FirebaseGateway.collection("driver_logs")), { rows ->
            val all = rows.map { it.id }
            val allNames = rows.associate { it.id to field(it.data, "companyName") }
            if (all != servers || allNames != names) { servers = all; names = allNames; listenToLogs() }
        }, { message -> element("logs-summary")?.textContent = message })
        render()
    }
}
